import { Card, CardContent, CardFooter, CardHeader } from '@ui/card.tsx';
import { Badge } from '@ui/badge.tsx';
import defaultProductImage from '@r/assets/images/fiddy/default-product-image.png';
import { FormattedHtml } from '@r/components/formatted-html.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import { useTranslation } from 'react-i18next';
import { CoinsIcon } from 'lucide-react';
import type { ProductProductReadModel } from '@r/api/generated/client.schemas.ts';
import { isOutOfStock } from '@r/utils/product-stock.ts';
import { cn } from '@r/utils/utils.ts';
import { ProductStockStatus } from './product-stock-status.tsx';

type Product = ProductProductReadModel;

const Product = ({ product }: { product: Product }) => {
  const { t } = useTranslation();
  const outOfStock = isOutOfStock(product.stock);

  return (
    <Card
      className={cn(
        'h-full border-0 sm:grid grid-cols-12 overflow-hidden rounded-none sm:rounded-3xl',
        outOfStock && 'bg-muted/40 opacity-60'
      )}
    >
      {product && (
        <>
          <div className="col-span-4 flex items-center justify-center p-7 relative">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t sm:bg-gradient-to-l from-muted-foreground/10 to-transparent to-50%" />
            <img
              className="object-cover object-center w-full max-w-[400px] max-h-[400px]"
              src={product.images?.[0]?.pathUri || defaultProductImage}
              alt={`${product.name} illustration`}
              onError={(e) => {
                e.currentTarget.src = defaultProductImage;
                e.currentTarget.onerror = null;
              }}
            />
          </div>

          <div className="col-span-8">
            <CardHeader className="space-y-2">
              {product.category && (
                <Badge className="w-fit font-medium">{product.category.label}</Badge>
              )}
              {product.brand && (
                <p className="text-xs font-medium text-foreground">{product.brand.name}</p>
              )}
              <FormattedHtml
                as="h1"
                text={product.name}
                className="text-2xl font-medium line-clamp-2"
              />

              <FormattedHtml
                text={product.designationCom}
                className="text-sm text-muted-foreground/60 line-clamp-2"
              />
            </CardHeader>

            <CardContent>
              <FormattedHtml
                as="div"
                text={product.description}
                className="text-sm text-foreground/80"
              />

              {product.ean && (
                <div className="mt-4 text-muted-foreground/60 font-light">
                  <span>{t('shop.product.ean.label')}&nbsp;:</span> {product.ean}
                </div>
              )}
              <ProductStockStatus
                stock={product.stock}
                lowStockAlert={product.lowStockAlert}
                className="mt-4 w-fit"
              />
            </CardContent>

            <CardFooter>
              {product.price && (
                <div className="mt-4 space-y-2">
                  <div>{t('shop.product.price.label')}:&nbsp;</div>
                  <div className="flex items-center gap-2 text-2xl font-bold">
                    <CoinsIcon />
                    {pointsTranslation(t, product.pointUnitPrice, true)}
                  </div>
                </div>
              )}
            </CardFooter>
          </div>
        </>
      )}
    </Card>
  );
};

export { Product };
